import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Switch,
  Text,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { GCModal } from "../modals/GCModal";
import { CloseCircleIcon } from "../icons/CloseIcon";
import { CaretLeftIcon } from "../icons/CaretLeftIcon";
import { postCreateComicList, postCreateReleaseList } from "../../api/lists";
import { READING_LISTS_SHELF_QUERY_KEY } from "../../screens/shelf/hooks/useReadingListsShelf";
import { WizardStepIndicator } from "./WizardStepIndicator";
import { ReadingListContentPickerBody } from "./picker/ReadingListContentPicker";
import type { PickerItem } from "./picker/types";

const TOTAL_STEPS = 3;
const NAME_MAX = 128;

type WizardStep = 1 | 2 | 3;

type WizardListType = "comics" | "releases";

interface CreateReadingListWizardProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CreateReadingListWizard({
  isOpen,
  onClose,
}: CreateReadingListWizardProps) {
  const [t] = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<WizardStep>(1);
  const [listType, setListType] = useState<WizardListType>("comics");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isPublic, setIsPublic] = useState(true);
  const [selectedItems, setSelectedItems] = useState<PickerItem[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedIds = useMemo(
    () => new Set(selectedItems.map((c) => c.id)),
    [selectedItems],
  );

  const resetState = () => {
    setStep(1);
    setListType("comics");
    setName("");
    setDescription("");
    setIsPublic(true);
    setSelectedItems([]);
    setIsSubmitting(false);
  };

  const handleListTypeChange = (next: WizardListType) => {
    if (next === listType) return;
    setListType(next);
    setSelectedItems([]);
  };

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
    setTimeout(resetState, 200);
  };

  const handleNext = () => {
    if (step < TOTAL_STEPS) setStep((step + 1) as 1 | 2 | 3);
  };
  const handleBack = () => {
    if (step > 1) setStep((step - 1) as 1 | 2 | 3);
  };

  const handleToggle = (item: PickerItem) => {
    setSelectedItems((curr) => {
      if (curr.some((c) => c.id === item.id))
        return curr.filter((c) => c.id !== item.id);
      return [...curr, item];
    });
  };

  const isNameValid = name.trim().length > 0;
  const canAdvance = step === 1 ? isNameValid : true;

  const handleCreate = async () => {
    if (!isNameValid || isSubmitting) return;
    setIsSubmitting(true);

    try {
      // Single-call create-with-items — backend's /v1/lists/create accepts
      // an optional { entity_type, list_ids } payload (mirroring add-to-lists
      // naming) and returns `items_added` + `items_failed` alongside the new
      // list. Saves us a round-trip per selected item.
      const hasItems = selectedItems.length > 0;
      const payload = {
        name: name.trim(),
        description: description.trim() || undefined,
        is_public: isPublic,
        ...(hasItems
          ? {
              entity_type: (listType === "comics" ? "comic" : "release") as
                | "comic"
                | "release",
              list_ids: selectedItems.map((c) => c.id),
            }
          : {}),
      };
      const createResponse =
        listType === "comics"
          ? await postCreateComicList(payload)
          : await postCreateReleaseList(payload);
      const newList = createResponse.data.payload.results;

      const failedCount = newList.items_failed?.length ?? 0;

      queryClient.invalidateQueries({
        queryKey: READING_LISTS_SHELF_QUERY_KEY,
      });
      // Also nuke the legacy per-type caches AddToListButton reads from
      // (see hooks/useListMutations.ts) — without this a list created
      // here doesn't show up in the bookmark popover on a comic/release
      // page until that popover's 30s staleTime expires.
      queryClient.invalidateQueries({
        queryKey: [`user-${listType === "comics" ? "comic" : "release"}-lists`],
      });

      if (failedCount > 0) {
        toast({
          title: t(
            "screens.shelf.readingLists.wizard.toasts.createdAddPartial",
            {
              name: newList.display_name,
              failedCount,
              totalCount: selectedItems.length,
            },
          ),
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: t("screens.shelf.readingLists.wizard.toasts.created", {
            name: newList.display_name,
          }),
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }

      onClose();
      setTimeout(resetState, 200);
    } catch {
      toast({
        title: t("screens.shelf.readingLists.wizard.toasts.createError"),
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      setIsSubmitting(false);
    }
  };

  const header = (
    <Flex align="center" justify="space-between" px="22px" py="16px">
      <Text
        fontFamily="Bricolage Grotesque"
        fontWeight={500}
        fontSize="15px"
        color="dune.dune"
      >
        {t("screens.shelf.readingLists.wizard.title")}
      </Text>
      <IconButton
        aria-label={t("screens.shelf.readingLists.wizard.closeAria")}
        icon={<CloseCircleIcon w="16px" h="16px" />}
        size="sm"
        variant="ghost"
        color="dune.700"
        _hover={{ bg: "transparent.white.10", color: "dune.dune" }}
        onClick={handleClose}
        isDisabled={isSubmitting}
      />
    </Flex>
  );

  const body = (
    <Flex direction="column">
      <Box px="22px" pt="18px">
        <WizardStepIndicator stepCount={TOTAL_STEPS} currentStep={step} />
      </Box>
      <Box px="32px" py="24px" minH="360px">
        {step === 1 && (
          <StepName
            value={name}
            onChange={setName}
            listType={listType}
            onListTypeChange={handleListTypeChange}
          />
        )}
        {step === 2 && (
          <StepDescription value={description} onChange={setDescription} />
        )}
        {step === 3 && (
          <StepItems
            listType={listType}
            selectedIds={selectedIds}
            onToggle={handleToggle}
            isPublic={isPublic}
            onPrivacyChange={setIsPublic}
          />
        )}
      </Box>
    </Flex>
  );

  const footer = (
    <Flex
      align="center"
      justify="space-between"
      px="22px"
      py="16px"
      bg="transparent.white.05"
      gap="12px"
    >
      {step > 1 ? (
        <Button
          variant="tertiary"
          size="sm"
          leftIcon={<CaretLeftIcon w="14px" h="14px" />}
          onClick={handleBack}
          isDisabled={isSubmitting}
        >
          {t("screens.shelf.readingLists.wizard.back")}
        </Button>
      ) : (
        <Box />
      )}
      <Flex gap="8px">
        {step < TOTAL_STEPS ? (
          <Button
            variant="primary"
            size="sm"
            onClick={handleNext}
            isDisabled={!canAdvance || isSubmitting}
          >
            {t("screens.shelf.readingLists.wizard.next")}
          </Button>
        ) : (
          <Button
            variant="primary"
            size="sm"
            onClick={handleCreate}
            isDisabled={!isNameValid}
            isLoading={isSubmitting}
          >
            {t("screens.shelf.readingLists.wizard.create")}
          </Button>
        )}
      </Flex>
    </Flex>
  );

  return (
    <GCModal
      isOpen={isOpen}
      onClose={handleClose}
      header={header}
      body={body}
      footer={footer}
      stickyFooter
      width="560px"
      maxWidth="560px"
      locked={isSubmitting}
    />
  );
}

interface StepNameProps {
  value: string;
  onChange: (v: string) => void;
  listType: WizardListType;
  onListTypeChange: (v: WizardListType) => void;
}

function StepName({
  value,
  onChange,
  listType,
  onListTypeChange,
}: StepNameProps) {
  const [t] = useTranslation();
  return (
    <Flex direction="column" gap="22px">
      <StepIntro
        heading={t("screens.shelf.readingLists.wizard.step1.heading")}
        description={t("screens.shelf.readingLists.wizard.step1.description")}
      />
      <Box>
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={t("screens.shelf.readingLists.wizard.step1.placeholder")}
          autoFocus
          maxLength={NAME_MAX}
          size="lg"
          bg="transparent.white.05"
          border="1px solid"
          borderColor="transparent.white.20"
          borderRadius="10px"
          color="dune.dune"
          fontSize="16px"
          _placeholder={{ color: "dune.800" }}
          _focus={{
            borderColor: "blaze.blaze",
            bg: "transparent.white.10",
            boxShadow: "none",
          }}
        />
        <Flex justify="space-between" mt="10px">
          <Text fontSize="11px" color="dune.700" letterSpacing="0.2px">
            {t("screens.shelf.readingLists.wizard.step1.required")}
          </Text>
          <Text fontSize="11px" color="dune.700" letterSpacing="0.2px">
            {t("screens.shelf.readingLists.wizard.step1.counter", {
              count: value.length,
              max: NAME_MAX,
            })}
          </Text>
        </Flex>
      </Box>
      <ListTypeSelector listType={listType} onChange={onListTypeChange} />
    </Flex>
  );
}

interface ListTypeSelectorProps {
  listType: WizardListType;
  onChange: (v: WizardListType) => void;
}

function ListTypeSelector({ listType, onChange }: ListTypeSelectorProps) {
  const [t] = useTranslation();
  return (
    <Flex
      bg="transparent.white.05"
      border="1px solid"
      borderColor="transparent.white.10"
      borderRadius="10px"
      p="4px"
      gap="4px"
      // Tiles share a row on desktop (md+); stack vertically on mobile
      // so the helper text ("Collect whole comic series" / "Collect
      // individual issues or chapters") doesn't wrap to 3 lines inside
      // each cramped tile.
      direction={{ base: "column", md: "row" }}
    >
      <ListTypeOption
        isActive={listType === "comics"}
        label={t("screens.shelf.readingLists.wizard.listType.comicsLabel")}
        helper={t("screens.shelf.readingLists.wizard.listType.comicsHelper")}
        onClick={() => onChange("comics")}
      />
      <ListTypeOption
        isActive={listType === "releases"}
        label={t("screens.shelf.readingLists.wizard.listType.releasesLabel")}
        helper={t("screens.shelf.readingLists.wizard.listType.releasesHelper")}
        onClick={() => onChange("releases")}
      />
    </Flex>
  );
}

function ListTypeOption({
  isActive,
  label,
  helper,
  onClick,
}: {
  isActive: boolean;
  label: string;
  helper: string;
  onClick: () => void;
}) {
  return (
    <Flex
      as="button"
      type="button"
      onClick={onClick}
      direction="column"
      align="flex-start"
      flex="1"
      px="12px"
      py="10px"
      borderRadius="8px"
      bg={isActive ? "transparent.white.10" : "transparent"}
      border="1px solid"
      borderColor={isActive ? "blaze.blaze" : "transparent"}
      transition="all 150ms ease-out"
      _hover={!isActive ? { bg: "transparent.white.05" } : undefined}
      textAlign="left"
    >
      <Text
        fontSize="13px"
        fontWeight={500}
        color={isActive ? "dune.dune" : "dune.100"}
      >
        {label}
      </Text>
      <Text fontSize="11px" color="dune.700" mt="2px">
        {helper}
      </Text>
    </Flex>
  );
}

interface StepDescriptionProps {
  value: string;
  onChange: (v: string) => void;
}

function StepDescription({ value, onChange }: StepDescriptionProps) {
  const [t] = useTranslation();
  return (
    <Flex direction="column" gap="22px">
      <StepIntro
        heading={t("screens.shelf.readingLists.wizard.step2.heading")}
        description={t("screens.shelf.readingLists.wizard.step2.description")}
      />
      <Box>
        <Textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={t("screens.shelf.readingLists.wizard.step2.placeholder")}
          minH="120px"
          resize="vertical"
          bg="transparent.white.05"
          border="1px solid"
          borderColor="transparent.white.20"
          borderRadius="10px"
          color="dune.dune"
          fontSize="16px"
          _placeholder={{ color: "dune.800" }}
          _focus={{
            borderColor: "blaze.blaze",
            bg: "transparent.white.10",
            boxShadow: "none",
          }}
        />
        <Text mt="10px" fontSize="11px" color="dune.700" letterSpacing="0.2px">
          {t("screens.shelf.readingLists.wizard.step2.optional")}
        </Text>
      </Box>
    </Flex>
  );
}

interface StepItemsProps {
  listType: WizardListType;
  selectedIds: Set<number>;
  onToggle: (item: PickerItem) => void;
  isPublic: boolean;
  onPrivacyChange: (next: boolean) => void;
}

function StepItems({
  listType,
  selectedIds,
  onToggle,
  isPublic,
  onPrivacyChange,
}: StepItemsProps) {
  const [t] = useTranslation();
  const headingKey =
    listType === "comics"
      ? "screens.shelf.readingLists.wizard.step3.comicsHeading"
      : "screens.shelf.readingLists.wizard.step3.releasesHeading";
  const descriptionKey =
    listType === "comics"
      ? "screens.shelf.readingLists.wizard.step3.comicsDescription"
      : "screens.shelf.readingLists.wizard.step3.releasesDescription";

  return (
    <Flex direction="column" gap="16px">
      <StepIntro heading={t(headingKey)} description={t(descriptionKey)} />
      {/*
        PrivacyRow lives ABOVE the picker, not below it. The picker's
        results feed is effectively unbounded (infinite scroll), so a
        toggle docked beneath it would scroll out of reach — and a
        "scroll to the bottom of every comic on the platform to flip a
        switch" UX is obviously broken. Keeping it under the heading
        also reads as "set the rules first, then pick the books."
      */}
      <PrivacyRow isPublic={isPublic} onChange={onPrivacyChange} />
      {/*
        Picker mounts inline — the wizard step IS the picker. Pass a noop
        onClose; the wizard's own back/next buttons drive navigation.
        disableInternalScroll lets the picker grid grow naturally and
        defers scrolling to the wizard's outer scroll container — without
        this we'd have a nested scroll fighting GCModal's, plus the
        wizard's sticky footer would be blocked by the bounded picker box.
      */}
      <Box
        bg="transparent.white.05"
        border="1px solid"
        borderColor="transparent.white.10"
        borderRadius="10px"
        p="14px"
        display="flex"
        flexDirection="column"
      >
        <ReadingListContentPickerBody
          isOpen
          onClose={() => {}}
          listType={listType}
          addedIds={selectedIds}
          onToggle={onToggle}
          disableInternalScroll
        />
      </Box>
    </Flex>
  );
}

interface PrivacyRowProps {
  isPublic: boolean;
  onChange: (v: boolean) => void;
}

function PrivacyRow({ isPublic, onChange }: PrivacyRowProps) {
  const [t] = useTranslation();
  return (
    <FormControl
      display="grid"
      gridTemplateColumns="auto 1fr"
      gap="12px"
      alignItems="center"
      p="12px 14px"
      border="1px solid"
      borderColor="transparent.white.10"
      borderRadius="10px"
      bg="transparent.white.05"
    >
      <Switch
        id="reading-list-wizard-privacy"
        size="sm"
        variant="brandSwitch"
        isChecked={isPublic}
        onChange={(e) => onChange(e.target.checked)}
      />
      <FormLabel htmlFor="reading-list-wizard-privacy" m={0} cursor="pointer">
        <Text fontSize="13px" fontWeight={500} color="dune.dune">
          {isPublic
            ? t("screens.shelf.readingLists.wizard.step3.privacyOnLabel")
            : t("screens.shelf.readingLists.wizard.step3.privacyOffLabel")}
        </Text>
        <Text fontSize="11px" color="dune.700" mt="2px">
          {isPublic
            ? t("screens.shelf.readingLists.wizard.step3.privacyOnHelper")
            : t("screens.shelf.readingLists.wizard.step3.privacyOffHelper")}
        </Text>
      </FormLabel>
    </FormControl>
  );
}

function StepIntro({
  heading,
  description,
}: {
  heading: string;
  description: string;
}) {
  return (
    <Flex direction="column" gap="8px">
      <Text
        fontFamily="Bricolage Grotesque"
        fontWeight={500}
        fontSize="28px"
        lineHeight="1.15"
        color="dune.dune"
        letterSpacing="-0.01em"
      >
        {heading}
      </Text>
      <Text fontSize="14px" lineHeight="1.5" color="dune.700">
        {description}
      </Text>
    </Flex>
  );
}
